import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  onSnapshot,
  getDoc,
  setDoc,
  orderBy,
  limit,
  enableNetwork,
  getDocs,
  collectionGroup,
  writeBatch,
  increment,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  Timestamp,
  getDocFromServer
} from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { TAB_ID } from "./syncService";
import { ReportTemplate, Phrase, Category, DEFAULT_CATEGORIES, DEFAULT_REPORTS, DEFAULT_PHRASES, ChatSession, GeminiContent, UserProfile, PeerMessage, SavedTranscription, ChatSummary, PeerChat, UIState, UserActivityStats, SystemPricing, ROIEventType, ROIEvent, AppSuggestion, ModuleRating, AppNotice, UserMessage, WorkGroup, WorkGroupMessage, GroupJoinRequest, LatencyEvent, RegionalRestriction } from "../types";
import { PRICING_MAP, DEFAULT_ROI_TIMES } from "../constants";

export const saveLatencyEvent = async (event: Omit<LatencyEvent, 'id' | 'timestamp' | 'userId'>) => {
  if (!db) return;
  const userId = auth.currentUser?.uid || 'anonymous';
  try {
    await addDoc(collection(db, "system", "performance", "latency"), sanitizeForFirestore({
      ...event,
      userId,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.error("Error saving latency event:", e);
  }
};

export const subscribeToLatency = (callback: (data: LatencyEvent[]) => void, limitCount: number = 100) => {
  if (!db) { callback([]); return () => {}; }
  const q = query(
    collection(db, "system", "performance", "latency"), 
    orderBy("timestamp", "desc"),
    limit(limitCount)
  );
  return onSnapshot(q, (snapshot) => {
    const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as LatencyEvent));
    callback(data);
  }, (error) => {
    handleFirestoreError(error, OperationType.LIST, "system/performance/latency");
    callback([]);
  });
};

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
    tenantId: string | null | undefined;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  }
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const message = error instanceof Error ? error.message : String(error);
  console.warn(`[Firestore ${operationType}] ${path || 'unknown'}: ${message}`);
}

/**
 * Sanitización recursiva robusta para Firestore con protección contra ciclos y funciones.
 * Elimina físicamente cualquier llave con valor 'undefined' para evitar errores críticos de setDoc.
 */
const sanitizeForFirestore = (obj: any, seen = new WeakSet()): any => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (seen.has(obj)) return undefined;
  if (obj instanceof Date || obj instanceof Timestamp) return obj;
  seen.add(obj);

  if (Array.isArray(obj)) {
    return obj
      .map(item => sanitizeForFirestore(item, seen))
      .filter(item => item !== undefined);
  }
  
  const newObj: any = {};
  Object.keys(obj).forEach(key => {
    const value = obj[key];
    if (value !== undefined && typeof value !== 'function') {
      const sanitized = sanitizeForFirestore(value, seen);
      if (sanitized !== undefined) {
        newObj[key] = sanitized;
      }
    }
  });
  return newObj;
};

const triggerUpdate = (key: string) => {
  window.dispatchEvent(new Event(`local-db-update-${key}`));
};

export const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
  const isSmallScreen = window.innerWidth < 1024;
  // Si es pantalla pequeña Y tiene touch, o es un UA móvil, lo consideramos móvil
  return isMobileUA || (isSmallScreen && ('ontouchstart' in window || navigator.maxTouchPoints > 0));
};

const isOffline = (userId?: string) => {
  if (!db) return true;
  if (userId === 'guest_dev_offline') return true;
  return false;
};

// Pricing and ROI constants moved to constants.ts

export const saveSystemPricing = async (pricing: SystemPricing) => {
  if (!db) return;
  const ref = doc(db, "system", "config");
  await setDoc(ref, sanitizeForFirestore({ pricing }), { merge: true });
};

export const subscribeToSystemPricing = (callback: (pricing: SystemPricing | null) => void) => {
  if (!db) { callback(null); return () => {}; }
  const ref = doc(db, "system", "config");
  return onSnapshot(ref, (doc) => {
    if (doc.exists() && doc.data().pricing) {
      const data = doc.data().pricing as SystemPricing;
      // Guardar en localStorage para acceso rápido sin red
      localStorage.setItem('radia_dynamic_pricing', JSON.stringify(data));
      callback(data);
    } else {
      callback(null);
    }
  }, (error) => {
    console.warn("Pricing subscription issue (likely permissions or offline):", error);
    const saved = localStorage.getItem('radia_dynamic_pricing');
    if (saved) {
      try {
        callback(JSON.parse(saved) as SystemPricing);
      } catch (e) {
        callback(null);
      }
    } else {
      callback(null);
    }
  });
};

export const recordROIEvent = async (userId: string, type: ROIEventType, complexity: 'SIMPLE' | 'COMPLEX' | 'PLUS' = 'SIMPLE', metadata?: any) => {
  if (isOffline(userId)) return;

  // Obtener configuración de ROI (desde localStorage o por defecto)
  let hoursSaved = (DEFAULT_ROI_TIMES[type] || 15) / 60; 
  try {
    const saved = localStorage.getItem('radia_dynamic_pricing');
    if (saved) {
      const dynamic = JSON.parse(saved) as SystemPricing;
      if (dynamic.roiConfig?.times[type]) {
        hoursSaved = dynamic.roiConfig.times[type] / 60; // Convertir minutos a horas
      }
    }
  } catch (e) {
    console.warn("Error getting ROI config:", e);
  }

  try {
    await addDoc(collection(db, "users", userId, "roi_events"), sanitizeForFirestore({
      type,
      complexity,
      hoursSaved,
      timestamp: Date.now(),
      metadata
    }));
  } catch (e) {
    console.error("Error recording ROI event:", e);
  }
};

export const recordTokenUsage = async (userId: string, model: string, input: number, output: number, hasSearch: boolean) => {
  if (isOffline(userId)) return;
  
  let pricing = PRICING_MAP[model] || (model.includes('pro') ? PRICING_MAP['default-pro'] : PRICING_MAP['default-flash']);
  let searchRate = hasSearch ? 0.035 : 0;

  try {
    const saved = localStorage.getItem('radia_dynamic_pricing');
    if (saved) {
      const dynamic = JSON.parse(saved) as SystemPricing;
      if (dynamic.models[model]) pricing = dynamic.models[model];
      if (dynamic.searchFlatFee) searchRate = hasSearch ? dynamic.searchFlatFee : 0;
    }
  } catch (e) {
    console.warn("Error parsing dynamic pricing from localStorage:", e);
  }

  const estimatedCost = (input * pricing.input) + (output * pricing.output) + searchRate;
  
  try {
    await addDoc(collection(db, "users", userId, "token_usage"), sanitizeForFirestore({
      model,
      inputTokens: input,
      outputTokens: output,
      hasSearch,
      estimatedCost,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.error("Fallo al registrar contabilidad de tokens", e);
  }
};

export const fetchUserRealStats = async (userId: string, startDate: number = 0): Promise<UserActivityStats> => {
  const emptyStats: UserActivityStats = { 
    reports: 0, 
    reportsByComplexity: { simple: 0, complex: 0, plus: 0 },
    phrases: 0, 
    transcriptions: 0, 
    chats: 0, 
    drafting: 0, 
    phdAssistance: 0,
    totalUsage: 0,
    tokens: { 
        totalInput: 0, 
        totalOutput: 0, 
        totalCost: 0, 
        searchCount: 0,
        byModel: {}
    },
    weeklyROI: [],
    dailyROI: [],
    monthlyROI: [],
    yearlyROI: [],
    modelHistory: {},
    roiEvents: []
  };
  
  if (isOffline(userId)) return emptyStats;
  
  try {
    const snaps = await Promise.all([
      getDocs(collection(db, "users", userId, "reports")).catch(() => ({ docs: [] })),
      getDocs(collection(db, "users", userId, "phrases")).catch(() => ({ docs: [] })),
      getDocs(collection(db, "users", userId, "transcriptions")).catch(() => ({ docs: [] })),
      getDocs(collection(db, "users", userId, "chat_sessions")).catch(() => ({ docs: [] })),
      getDocs(collection(db, "users", userId, "drafting_usage")).catch(() => ({ docs: [] })),
      getDocs(collection(db, "users", userId, "token_usage")).catch(() => ({ docs: [] })),
      getDocs(collection(db, "users", userId, "roi_events")).catch(() => ({ docs: [] }))
    ]);

    const [reportsSnap, phrasesSnap, transSnap, chatsSnap, draftingSnap, tokensSnap, roiSnap] = snaps as any[];

    const filteredReports = reportsSnap.docs
        .map(d => ({...d.data(), id: d.id} as ReportTemplate))
        .filter(r => r.createdAt >= startDate);

    const reportsByComplexity = { simple: 0, complex: 0, plus: 0 };
    filteredReports.forEach(data => {
      const plainText = (data.content || "").replace(/<[^>]*>?/gm, ' ');
      const wordCount = plainText.split(/\s+/).filter(Boolean).length;
      if (data.filterIds?.includes('f2')) reportsByComplexity.plus++;
      else if (data.filterIds?.includes('f1') || wordCount > 150) reportsByComplexity.complex++;
      else reportsByComplexity.simple++;
    });

    const hourlyROI: { label: string, hours: number, timestamp: number }[] = [];
    const dailyROI: { label: string, hours: number, timestamp: number }[] = [];
    const weeklyROI: { label: string, hours: number, timestamp: number }[] = [];
    const monthlyROI: { label: string, hours: number, timestamp: number }[] = [];
    const yearlyROI: { label: string, hours: number, timestamp: number }[] = [];
    const roiEvents = roiSnap.docs.map(d => d.data() as ROIEvent);

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonthIdx = now.getMonth();

    // 1. HOY (Hourly): 00:00 to 23:59
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    for (let i = 0; i < 24; i++) {
        const hStart = todayStart + (i * 60 * 60 * 1000);
        const hEnd = hStart + (60 * 60 * 1000) - 1;
        const hourLabel = i.toString().padStart(2, '0') + ':00';
        const hHours = roiEvents
            .filter(e => e.timestamp >= hStart && e.timestamp <= hEnd)
            .reduce((acc, curr) => acc + (curr.hoursSaved || 0), 0);
        hourlyROI.push({ label: hourLabel, hours: hHours, timestamp: hStart });
    }

    // 2. DIARIO (Daily): 1st to last day of current month
    const firstDayOfMonth = new Date(currentYear, currentMonthIdx, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonthIdx + 1, 0);
    for (let d = 1; d <= lastDayOfMonth.getDate(); d++) {
        const dayStart = new Date(currentYear, currentMonthIdx, d).getTime();
        const dayEnd = dayStart + (24 * 60 * 60 * 1000) - 1;
        const dayLabel = d.toString().padStart(2, '0');
        const dHours = roiEvents
            .filter(e => e.timestamp >= dayStart && e.timestamp <= dayEnd)
            .reduce((acc, curr) => acc + (curr.hoursSaved || 0), 0);
        dailyROI.push({ label: dayLabel, hours: dHours, timestamp: dayStart });
    }

    // 3. MESES (Monthly): Jan to Dec of current year
    const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    for (let m = 0; m < 12; m++) {
        const mStart = new Date(currentYear, m, 1).getTime();
        const mEnd = new Date(currentYear, m + 1, 0, 23, 59, 59, 999).getTime();
        const mHours = roiEvents
            .filter(e => e.timestamp >= mStart && e.timestamp <= mEnd)
            .reduce((acc, curr) => acc + (curr.hoursSaved || 0), 0);
        monthlyROI.push({ label: monthNames[m], hours: mHours, timestamp: mStart });
    }

    // 4. ANUAL (Yearly): Last 5 years
    for (let y = currentYear - 4; y <= currentYear; y++) {
        const yStart = new Date(y, 0, 1).getTime();
        const yEnd = new Date(y, 11, 31, 23, 59, 59, 999).getTime();
        const yHours = roiEvents
            .filter(e => e.timestamp >= yStart && e.timestamp <= yEnd)
            .reduce((acc, curr) => acc + (curr.hoursSaved || 0), 0);
        yearlyROI.push({ label: y.toString(), hours: yHours, timestamp: yStart });
    }

    // Semanales (keeping it for compatibility, but focusing on the requested ones)
    const dayMs = 24 * 60 * 60 * 1000;
    const weekMs = 7 * dayMs;
    const effectiveStartDate = startDate > 0 ? startDate : (Date.now() - 30 * dayMs);
    for (let t = effectiveStartDate; t <= Date.now(); t += weekMs) {
        const weekLabel = `S-${Math.floor((t - effectiveStartDate) / weekMs) + 1}`;
        const weekStart = t;
        const weekEnd = t + weekMs;
        const weekHours = roiEvents
            .filter(e => e.timestamp >= weekStart && e.timestamp <= weekEnd)
            .reduce((acc, curr) => acc + (curr.hoursSaved || 0), 0);
        weeklyROI.push({ label: weekLabel, hours: weekHours, timestamp: weekStart });
    }
    
    const tokenStats = { 
        totalInput: 0, 
        totalOutput: 0, 
        totalCost: 0, 
        searchCount: 0,
        byModel: {} as Record<string, { input: number; output: number; cost: number }>
    };

    const modelHistory: Record<string, { date: string, tokens: number }[]> = {};

    tokensSnap.docs.forEach(doc => {
        const d = doc.data();
        if (d.timestamp < startDate) return;

        const model = d.model || 'unknown';
        const input = d.inputTokens || 0;
        const output = d.outputTokens || 0;
        const cost = d.estimatedCost || 0;

        tokenStats.totalInput += input;
        tokenStats.totalOutput += output;
        tokenStats.totalCost += cost;
        if (d.hasSearch) tokenStats.searchCount++;

        if (!tokenStats.byModel[model]) {
            tokenStats.byModel[model] = { input: 0, output: 0, cost: 0 };
        }
        tokenStats.byModel[model].input += input;
        tokenStats.byModel[model].output += output;
        tokenStats.byModel[model].cost += cost;

        if (!modelHistory[model]) modelHistory[model] = [];
        const dateStr = new Date(d.timestamp).toLocaleDateString([], {day: '2-digit', month: '2-digit'});
        const existingDate = modelHistory[model].find(h => h.date === dateStr);
        if (existingDate) existingDate.tokens += (input + output);
        else modelHistory[model].push({ date: dateStr, tokens: (input + output) });
    });

    return {
      reports: filteredReports.length,
      reportsByComplexity,
      phrases: phrasesSnap.size,
      transcriptions: transSnap.size,
      chats: chatsSnap.size,
      drafting: draftingSnap.size,
      phdAssistance: roiEvents.filter(e => e.type === ROIEventType.PHD_ASSISTANCE).length,
      totalUsage: filteredReports.length + phrasesSnap.size + transSnap.size + chatsSnap.size + draftingSnap.size + roiEvents.filter(e => e.type === ROIEventType.PHD_ASSISTANCE).length,
      tokens: tokenStats,
      hourlyROI,
      weeklyROI,
      dailyROI,
      monthlyROI,
      yearlyROI,
      modelHistory,
      roiEvents
    };
  } catch (e) {
    console.error("Error fetching real stats:", e);
    return emptyStats;
  }
};

export const saveAppSuggestion = async (suggestion: Omit<AppSuggestion, 'id'>) => {
  if (!db) return;
  try {
    await addDoc(collection(db, "system", "feedback", "suggestions"), sanitizeForFirestore({
      ...suggestion,
      timestamp: Date.now(),
      status: 'PENDING'
    }));
  } catch (e) {
    console.error("Error saving suggestion:", e);
  }
};

export const subscribeToSuggestions = (callback: (data: AppSuggestion[]) => void) => {
  if (!db) { callback([]); return () => {}; }
  const q = query(collection(db, "system", "feedback", "suggestions"), orderBy("timestamp", "desc"));
  return onSnapshot(q, (snapshot) => {
    const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as AppSuggestion));
    callback(data);
  }, (error) => {
    console.warn("Suggestions subscription issue:", error);
    callback([]);
  });
};

export const saveModuleRating = async (rating: Omit<ModuleRating, 'id'>) => {
  if (!db) return;
  try {
    // Usar setDoc con ID compuesto para evitar duplicados del mismo usuario para el mismo módulo
    const ratingId = `${rating.userId}_${rating.module}`;
    const ref = doc(db, "system", "feedback", "ratings", ratingId);
    await setDoc(ref, sanitizeForFirestore({
      ...rating,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.error("Error saving rating:", e);
  }
};

export const subscribeToRatings = (callback: (data: ModuleRating[]) => void) => {
  if (!db) { callback([]); return () => {}; }
  const q = query(collection(db, "system", "feedback", "ratings"), orderBy("timestamp", "desc"));
  return onSnapshot(q, (snapshot) => {
    const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ModuleRating));
    callback(data);
  }, (error) => {
    console.warn("Ratings subscription issue:", error);
    callback([]);
  });
};

export const subscribeToAppNotices = (callback: (data: AppNotice[]) => void) => {
  if (!db) { callback([]); return () => {}; }
  const q = query(collection(db, "system", "config", "notices"), where("active", "==", true), orderBy("timestamp", "desc"));
  return onSnapshot(q, (snapshot) => {
    const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as AppNotice));
    callback(data);
  }, (error) => {
    console.warn("App notices subscription issue:", error);
    callback([]);
  });
};

export const saveAppNotice = async (notice: Omit<AppNotice, 'id'>) => {
  if (!db) return;
  try {
    await addDoc(collection(db, "system", "config", "notices"), sanitizeForFirestore({
      ...notice,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.error("Error saving notice:", e);
  }
};

export const subscribeToRegionalRestrictions = (callback: (data: RegionalRestriction[]) => void) => {
  if (!db) { callback([]); return () => {}; }
  const q = query(collection(db, "system", "config", "regional_restrictions"), orderBy("updatedAt", "desc"));
  return onSnapshot(q, (snapshot) => {
    const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as RegionalRestriction));
    callback(data);
  }, (error) => {
    handleFirestoreError(error, OperationType.LIST, "system/config/regional_restrictions");
  });
};

export const saveRegionalRestriction = async (restriction: Omit<RegionalRestriction, 'id' | 'updatedAt'> & { id?: string }) => {
  if (!db) return;
  try {
    const data = sanitizeForFirestore({
      type: restriction.type,
      value: restriction.value.trim(),
      disabledModules: restriction.disabledModules,
      renameConclusionToSummary: !!restriction.renameConclusionToSummary,
      disableDiferenciales: !!restriction.disableDiferenciales,
      updatedAt: Date.now()
    });
    if (restriction.id) {
      await setDoc(doc(db, "system", "config", "regional_restrictions", restriction.id), data);
    } else {
      await addDoc(collection(db, "system", "config", "regional_restrictions"), data);
    }
  } catch (e) {
    handleFirestoreError(e, OperationType.WRITE, `system/config/regional_restrictions/${restriction.id || 'new'}`);
  }
};

export const deleteRegionalRestriction = async (id: string) => {
  if (!db) return;
  try {
    await deleteDoc(doc(db, "system", "config", "regional_restrictions", id));
  } catch (e) {
    handleFirestoreError(e, OperationType.DELETE, `system/config/regional_restrictions/${id}`);
  }
};

export const sendUserMessage = async (userId: string, message: Omit<UserMessage, 'id' | 'read' | 'timestamp'>) => {
  if (!db) return;
  try {
    await addDoc(collection(db, "users", userId, "messages"), sanitizeForFirestore({
      ...message,
      timestamp: Date.now(),
      read: false
    }));
  } catch (e) {
    console.error("Error sending user message:", e);
  }
};

export const subscribeToUserMessages = (userId: string, callback: (data: UserMessage[]) => void) => {
  if (!db || isOffline(userId)) { callback([]); return () => {}; }
  const q = query(collection(db, "users", userId, "messages"), orderBy("timestamp", "desc"));
  return onSnapshot(q, (snapshot) => {
    const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as UserMessage));
    callback(data);
  }, (error) => {
    handleFirestoreError(error, OperationType.LIST, `users/${userId}/messages`);
    callback([]);
  });
};

export const markUserMessageAsRead = async (userId: string, messageId: string) => {
  if (!db) return;
  try {
    const ref = doc(db, "users", userId, "messages", messageId);
    await updateDoc(ref, { read: true });
  } catch (e) {
    console.error("Error marking message as read:", e);
  }
};

export const saveVoiceProfile = async (userId: string, profile: string) => {
  if (isOffline(userId)) return;
  const ref = doc(db, "users", userId, "preferences", "voice_profile");
  try {
    await setDoc(ref, sanitizeForFirestore({ profile, updatedAt: Date.now() }));
  } catch (e) {
    console.error("Cloud Voice Profile Save Error", e);
  }
};

export const getVoiceProfile = async (userId: string): Promise<string | null> => {
  if (isOffline(userId)) return null;
  const ref = doc(db, "users", userId, "preferences", "voice_profile");
  try {
    const snap = await getDoc(ref);
    if (snap.exists()) return snap.data().profile;
  } catch (e) {
    console.error("Cloud Voice Profile Fetch Error", e);
  }
  return null;
};

export const saveUIState = async (userId: string, state: UIState) => {
  if (isOffline(userId)) return;
  const ref = doc(db, "users", userId, "preferences", "ui_memory");
  try {
    await setDoc(ref, sanitizeForFirestore(state));
  } catch (e) {
    console.error("Could not save UI State to cloud", e);
  }
};

export const getUIState = async (userId: string): Promise<UIState | null> => {
  if (isOffline(userId)) return null;
  const ref = doc(db, "users", userId, "preferences", "ui_memory");
  try {
    const snap = await getDoc(ref);
    if (snap.exists()) return snap.data() as UIState;
  } catch (e) {
    console.error("Could not fetch UI State from cloud", e);
  }
  return null;
};

export const saveModular1Favorites = async (userId: string, favorites: string[]) => {
  if (isOffline(userId)) return;
  const ref = doc(db, "users", userId, "preferences", "modular1_favs");
  try {
    await setDoc(ref, { favorites, updatedAt: Date.now() });
  } catch (e) {
    console.error("Could not save Modular 1 favorites to cloud", e);
  }
};

export const subscribeToModular1Favorites = (userId: string, callback: (favorites: string[]) => void) => {
  if (isOffline(userId)) return () => {};
  const ref = doc(db, "users", userId, "preferences", "modular1_favs");
  return onSnapshot(ref, (snap) => {
    if (snap.exists()) {
      callback(snap.data().favorites || []);
    } else {
      callback([]);
    }
  }, (error) => {
    handleFirestoreError(error, OperationType.GET, `users/${userId}/preferences/modular1_favs`);
    callback([]);
  });
};

export const streamTextToCloud = async (userId: string, text: string, status: 'partial' | 'final' = 'partial') => {
  if (isOffline(userId)) return;
  console.log("Streaming text to cloud:", { text: text.substring(0, 20) + "...", status });
  const ref = doc(db, "users", userId, "live_data", "text_stream");
  try {
    await setDoc(ref, {
      text,
      status,
      timestamp: serverTimestamp(),
      senderId: auth.currentUser?.uid || 'unknown'
    });
  } catch (e) {
    console.error("Could not stream text to cloud", e);
  }
};

export const subscribeToTextStream = (userId: string, callback: (data: { text: string, status: string, timestamp: number } | null) => void) => {
  if (isOffline(userId)) return () => {};
  console.log("Subscribing to text stream for user:", userId);
  const ref = doc(db, "users", userId, "live_data", "text_stream");
  
  return onSnapshot(ref, (snap) => {
    if (snap.exists()) {
      const data = snap.data();
      console.log("Received text stream update:", { status: data.status, timestamp: data.timestamp });
      const timestamp = data.timestamp instanceof Timestamp ? data.timestamp.toMillis() : Date.now();
      callback({
        text: data.text,
        status: data.status,
        timestamp
      });
    } else {
      console.log("Text stream document does not exist yet.");
      callback(null);
    }
  }, (error) => {
    console.error("Text Stream Subscription Error:", error);
  });
};

export const updateUserPresence = async (userId: string) => {
  if (isOffline(userId)) return;
  if (!auth.currentUser || auth.currentUser.uid !== userId) return;

  const now = Date.now();
  const userRef = doc(db, "users", userId);
  const profileRef = doc(db, "users", userId, "profile", "main");
  const publicRef = doc(db, "users", userId, "profile", "public");

  // Alternativa A: Desacoplar escrituras en lugar de un writeBatch atómico.
  // Si un subdocumento aún no existe o experimenta restricciones de reglas,
  // la presencia en users/{userId} se aplica exitosamente sin romper la sesión.
  const writes = [
    setDoc(userRef, { lastSeenAt: now }, { merge: true }),
    setDoc(profileRef, { lastSeenAt: now }, { merge: true }),
    setDoc(publicRef, { lastSeenAt: now, uid: userId, isPublic: true }, { merge: true })
  ];

  const results = await Promise.allSettled(writes);
  const failed = results.filter(r => r.status === 'rejected');
  if (failed.length > 0) {
    // Registro silencioso en debug/info en vez de console.warn intrusivo para evitar banners de advertencia en UI
    console.debug("[updateUserPresence] Heartbeat partial sync:", failed.length, "writes failed out of", results.length);
  }
};

export const recordDraftingUsage = async (userId: string, actionType: 'audit' | 'apply') => {
  if (isOffline(userId)) return;
  try {
    await addDoc(collection(db, "users", userId, "drafting_usage"), sanitizeForFirestore({
      type: actionType,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.error("Error recording drafting usage:", e);
  }
};

export const subscribeToReports = (userId: string, callback: (data: ReportTemplate[]) => void) => {
  if (isOffline(userId)) {
    const key = `reports_${userId}`;
    const load = () => {
      const stored = localStorage.getItem(key);
      callback(stored ? JSON.parse(stored) : DEFAULT_REPORTS);
    };
    load();
    const handler = () => load();
    window.addEventListener(`local-db-update-${key}`, handler);
    return () => window.removeEventListener(`local-db-update-${key}`, handler);
  }
  const q = query(collection(db, "users", userId, "reports"), orderBy("updatedAt", "desc"));
  return onSnapshot(q, (snapshot) => {
    const reports = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ReportTemplate));
    callback(reports);
  }, (error) => {
    console.error("Error subscribing to reports:", error);
    callback([]);
  });
};

export const saveReportToCloud = async (userId: string, report: ReportTemplate) => {
  if (isOffline(userId)) {
    const key = `reports_${userId}`;
    const stored = localStorage.getItem(key);
    let items: ReportTemplate[] = stored ? JSON.parse(stored) : DEFAULT_REPORTS;
    const index = items.findIndex(r => r.id === report.id);
    if (index >= 0) items[index] = report;
    else items.unshift(report);
    localStorage.setItem(key, JSON.stringify(items));
    triggerUpdate(key);
    return;
  }
  const reportRef = doc(db, "users", userId, "reports", report.id);
  await setDoc(reportRef, sanitizeForFirestore(report));
};

export const incrementReportUsage = async (userId: string, reportId: string) => {
    if (isOffline(userId)) {
        const key = `reports_${userId}`;
        const stored = localStorage.getItem(key);
        if (stored) {
            const items: ReportTemplate[] = JSON.parse(stored);
            const item = items.find(r => r.id === reportId);
            if (item) {
                item.usageCount = (item.usageCount || 0) + 1;
                localStorage.setItem(key, JSON.stringify(items));
                triggerUpdate(key);
            }
        }
        return;
    }
    const ref = doc(db, "users", userId, "reports", reportId);
    await updateDoc(ref, { usageCount: increment(1) });
};

export const moveReportToTrash = async (userId: string, reportId: string) => {
    if (isOffline(userId)) {
        const key = `reports_${userId}`;
        const stored = localStorage.getItem(key);
        if (stored) {
            const items: ReportTemplate[] = JSON.parse(stored);
            const item = items.find(r => r.id === reportId);
            if (item) {
                item.deletedAt = Date.now();
                localStorage.setItem(key, JSON.stringify(items));
                triggerUpdate(key);
            }
        }
        return;
    }
    const ref = doc(db, "users", userId, "reports", reportId);
    await updateDoc(ref, sanitizeForFirestore({ deletedAt: Date.now() }));
};

export const restoreReportFromTrash = async (userId: string, reportId: string) => {
    if (isOffline(userId)) {
        const key = `reports_${userId}`;
        const stored = localStorage.getItem(key);
        if (stored) {
            const items: ReportTemplate[] = JSON.parse(stored);
            const item = items.find(r => r.id === reportId);
            if (item) {
                item.deletedAt = undefined;
                localStorage.setItem(key, JSON.stringify(items));
                triggerUpdate(key);
            }
        }
        return;
    }
    const ref = doc(db, "users", userId, "reports", reportId);
    await updateDoc(ref, sanitizeForFirestore({ deletedAt: null }));
};

export const permanentDeleteReport = async (userId: string, reportId: string) => {
  if (isOffline(userId)) {
    const key = `reports_${userId}`;
    const stored = localStorage.getItem(key);
    if (stored) {
       let items: ReportTemplate[] = JSON.parse(stored);
       items = items.filter(r => r.id !== reportId);
       localStorage.setItem(key, JSON.stringify(items));
       triggerUpdate(key);
    }
    return;
  }
  await deleteDoc(doc(db, "users", userId, "reports", reportId));
};

export const emptyReportTrash = async (userId: string) => {
    if (isOffline(userId)) {
        const key = `reports_${userId}`;
        const stored = localStorage.getItem(key);
        if (stored) {
            let items: ReportTemplate[] = JSON.parse(stored);
            items = items.filter(r => !r.deletedAt); 
            localStorage.setItem(key, JSON.stringify(items));
            triggerUpdate(key);
        }
        return;
    }
    const q = query(collection(db, "users", userId, "reports"), where("deletedAt", "!=", null));
    const snapshot = await getDocs(q);
    const batch = writeBatch(db);
    snapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
    });
    await batch.commit();
};

export const toggleFavoriteReportCloud = async (reportId: string, isFavorite: boolean, userId: string) => {
    if (isOffline(userId)) {
        const key = `reports_${userId}`;
        const stored = localStorage.getItem(key);
        if (stored) {
            const items: ReportTemplate[] = JSON.parse(stored);
            const item = items.find(r => r.id === reportId);
            if (item) {
                item.isFavorite = !isFavorite;
                localStorage.setItem(key, JSON.stringify(items));
                triggerUpdate(key);
            }
        }
        return;
    }
    const ref = doc(db, "users", userId, "reports", reportId);
    await updateDoc(ref, sanitizeForFirestore({ isFavorite: !isFavorite }));
};

export const subscribeToPhrases = (userId: string, callback: (data: Phrase[]) => void) => {
  if (isOffline(userId)) {
    const key = `phrases_${userId}`;
    const load = () => {
      const stored = localStorage.getItem(key);
      callback(stored ? JSON.parse(stored) : DEFAULT_PHRASES);
    };
    load();
    const handler = () => load();
    window.addEventListener(`local-db-update-${key}`, handler);
    return () => window.removeEventListener(`local-db-update-${key}`, handler);
  }
  const q = query(collection(db, "users", userId, "phrases"), orderBy("createdAt", "desc"));
  return onSnapshot(q, (snapshot) => {
    const phrases = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Phrase));
    callback(phrases);
  }, (error) => {
    handleFirestoreError(error, OperationType.LIST, `users/${userId}/phrases`);
    callback(DEFAULT_PHRASES);
  });
};

export const savePhraseToCloud = async (userId: string, phrase: Phrase) => {
  if (isOffline(userId)) {
    const key = `phrases_${userId}`;
    const stored = localStorage.getItem(key);
    let items: Phrase[] = stored ? JSON.parse(stored) : DEFAULT_PHRASES;
    const index = items.findIndex(p => p.id === phrase.id);
    if (index >= 0) items[index] = phrase;
    else items.unshift(phrase);
    localStorage.setItem(key, JSON.stringify(items));
    triggerUpdate(key);
    return;
  }
  const ref = doc(db, "users", userId, "phrases", phrase.id);
  await setDoc(ref, sanitizeForFirestore(phrase));
};

export const incrementPhraseUsage = async (userId: string, phraseId: string) => {
    if (isOffline(userId)) {
        const key = `phrases_${userId}`;
        const stored = localStorage.getItem(key);
        if (stored) {
            const items: Phrase[] = JSON.parse(stored);
            const item = items.find(p => p.id === phraseId);
            if (item) {
                item.usageCount = (item.usageCount || 0) + 1;
                localStorage.setItem(key, JSON.stringify(items));
                triggerUpdate(key);
            }
        }
        return;
    }
    const ref = doc(db, "users", userId, "phrases", phraseId);
    await updateDoc(ref, { usageCount: increment(1) });
};

export const movePhraseToTrash = async (userId: string, phraseId: string) => {
    if (isOffline(userId)) {
        const key = `phrases_${userId}`;
        const stored = localStorage.getItem(key);
        if (stored) {
            const items: Phrase[] = JSON.parse(stored);
            const item = items.find(p => p.id === phraseId);
            if (item) {
                item.deletedAt = Date.now();
                localStorage.setItem(key, JSON.stringify(items));
                triggerUpdate(key);
            }
        }
        return;
    }
    const ref = doc(db, "users", userId, "phrases", phraseId);
    await updateDoc(ref, sanitizeForFirestore({ deletedAt: Date.now() }));
};

export const restorePhraseFromTrash = async (userId: string, phraseId: string) => {
    if (isOffline(userId)) {
        const key = `phrases_${userId}`;
        const stored = localStorage.getItem(key);
        if (stored) {
            const items: Phrase[] = JSON.parse(stored);
            const item = items.find(p => p.id === phraseId);
            if (item) {
                item.deletedAt = undefined;
                localStorage.setItem(key, JSON.stringify(items));
                triggerUpdate(key);
            }
        }
        return;
    }
    const ref = doc(db, "users", userId, "phrases", phraseId);
    await updateDoc(ref, sanitizeForFirestore({ deletedAt: null }));
};

export const permanentDeletePhrase = async (userId: string, phraseId: string) => {
  if (isOffline(userId)) {
    const key = `phrases_${userId}`;
    const stored = localStorage.getItem(key);
    if (stored) {
       let items: Phrase[] = JSON.parse(stored);
       items = items.filter(p => p.id !== phraseId);
       localStorage.setItem(key, JSON.stringify(items));
       triggerUpdate(key);
    }
    return;
  }
  await deleteDoc(doc(db, "users", userId, "phrases", phraseId));
};

export const emptyPhraseTrash = async (userId: string) => {
    if (isOffline(userId)) {
        const key = `phrases_${userId}`;
        const stored = localStorage.getItem(key);
        if (stored) {
            let items: Phrase[] = JSON.parse(stored);
            items = items.filter(p => !p.deletedAt);
            localStorage.setItem(key, JSON.stringify(items));
            triggerUpdate(key);
        }
        return;
    }
    const q = query(collection(db, "users", userId, "phrases"), where("deletedAt", "!=", null));
    const snapshot = await getDocs(q);
    const batch = writeBatch(db);
    snapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
    });
    await batch.commit();
};

export const toggleFavoritePhraseCloud = async (phraseId: string, isFavorite: boolean, userId: string) => {
    if (isOffline(userId)) {
        const key = `phrases_${userId}`;
        const stored = localStorage.getItem(key);
        if (stored) {
            const items: Phrase[] = JSON.parse(stored);
            const item = items.find(p => p.id === phraseId);
            if (item) {
                item.isFavorite = !isFavorite;
                localStorage.setItem(key, JSON.stringify(items));
                triggerUpdate(key);
            }
        }
        return;
    }
    const ref = doc(db, "users", userId, "phrases", phraseId);
    await updateDoc(ref, sanitizeForFirestore({ isFavorite: !isFavorite }));
};

export const subscribeToTranscriptions = (userId: string, callback: (data: SavedTranscription[]) => void) => {
  if (isOffline(userId)) {
    const key = `transcriptions_${userId}`;
    const load = () => {
      const stored = localStorage.getItem(key);
      callback(stored ? JSON.parse(stored) : []);
    };
    load();
    const handler = () => load();
    window.addEventListener(`local-db-update-${key}`, handler);
    return () => window.removeEventListener(`local-db-update-${key}`, handler);
  }
  const q = query(collection(db, "users", userId, "transcriptions"), orderBy("createdAt", "desc"));
  return onSnapshot(q, (snapshot) => {
    const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SavedTranscription));
    callback(items);
  }, (error) => {
    console.error("Error subscribing to transcriptions:", error);
    callback([]);
  });
};

export const saveTranscriptionToCloud = async (userId: string, item: SavedTranscription) => {
  if (isOffline(userId)) {
    const key = `transcriptions_${userId}`;
    const stored = localStorage.getItem(key);
    let items: SavedTranscription[] = stored ? JSON.parse(stored) : [];
    
    const existingIndex = items.findIndex(t => t.id === item.id);
    if (existingIndex >= 0) {
      items[existingIndex] = item;
    } else {
      items = [item, ...items];
    }
    
    localStorage.setItem(key, JSON.stringify(items));
    triggerUpdate(key);
    return;
  }
  const ref = doc(db, "users", userId, "transcriptions", item.id);
  await setDoc(ref, sanitizeForFirestore(item));
};

export const deleteTranscriptionFromCloud = async (userId: string, id: string) => {
  if (isOffline(userId)) {
    const key = `transcriptions_${userId}`;
    const stored = localStorage.getItem(key);
    if (stored) {
       let items: SavedTranscription[] = JSON.parse(stored);
       items = items.filter(t => t.id !== id);
       localStorage.setItem(key, JSON.stringify(items));
       triggerUpdate(key);
    }
    return;
  }
  await deleteDoc(doc(db, "users", userId, "transcriptions", id));
};

export const clearTranscriptionsFromCloud = async (userId: string) => {
    if (isOffline(userId)) {
        const key = `transcriptions_${userId}`;
        localStorage.setItem(key, JSON.stringify([]));
        triggerUpdate(key);
        return;
    }
    const q = query(collection(db, "users", userId, "transcriptions"));
    const snapshot = await getDocs(q);
    const batch = writeBatch(db);
    snapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
    });
    await batch.commit();
};

export const subscribeToCategories = (userId: string, callback: (data: Category[]) => void) => {
  if (isOffline(userId)) {
    const key = `categories_${userId}`;
    const load = () => {
      const stored = localStorage.getItem(key);
      const items: Category[] = stored ? JSON.parse(stored) : DEFAULT_CATEGORIES;
      items.sort((a, b) => (a.order || 0) - (b.order || 0));
      callback(items);
    };
    load();
    const handler = () => load();
    window.addEventListener(`local-db-update-${key}`, handler);
    return () => window.removeEventListener(`local-db-update-${key}`, handler);
  }
  const q = collection(db, "users", userId, "categories");
  return onSnapshot(q, (snapshot) => {
    if (snapshot.empty) {
        callback(DEFAULT_CATEGORIES); 
    } else {
        const categories = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Category));
        categories.sort((a, b) => (a.order || 0) - (b.order || 0));
        callback(categories);
    }
  }, (error) => {
      console.error("Error suscribiéndose a categorías:", error);
      callback(DEFAULT_CATEGORIES);
  });
};

export const saveCategoryToCloud = async (userId: string, category: Category) => {
  if (isOffline(userId)) {
     const key = `categories_${userId}`;
     const stored = localStorage.getItem(key);
     let items: Category[] = stored ? JSON.parse(stored) : DEFAULT_CATEGORIES;
     const idx = items.findIndex(c => c.id === category.id);
     if (idx >= 0) items[idx] = category;
     else items.push(category);
     localStorage.setItem(key, JSON.stringify(items));
     triggerUpdate(key);
     return;
  }
  const ref = doc(db, "users", userId, "categories", category.id);
  await setDoc(ref, sanitizeForFirestore(category), { merge: true });
};

export const saveMultipleCategoriesToCloud = async (userId: string, categories: Category[]) => {
    if (isOffline(userId)) {
        const key = `categories_${userId}`;
        localStorage.setItem(key, JSON.stringify(categories));
        triggerUpdate(key);
        return;
    }
    const batch = writeBatch(db);
    categories.forEach((cat, index) => {
        const ref = doc(db, "users", userId, "categories", cat.id);
        batch.set(ref, sanitizeForFirestore({ ...cat, order: index }), { merge: true });
    });
    await batch.commit();
};

export const swapCategoriesOrder = async (userId: string, cat1: Category, cat2: Category) => {
    const o1 = cat1.order ?? 0;
    const o2 = cat2.order ?? 0;
    if (isOffline(userId)) {
        const key = `categories_${userId}`;
        const stored = localStorage.getItem(key);
        if (stored) {
            const items: Category[] = JSON.parse(stored);
            const i1 = items.findIndex(c => c.id === cat1.id);
            const i2 = items.findIndex(c => c.id === cat2.id);
            if (i1 >= 0 && i2 >= 0) {
                const tempOrder = items[i1].order ?? 0;
                items[i1].order = items[i2].order ?? 0;
                items[i2].order = tempOrder;
                localStorage.setItem(key, JSON.stringify(items));
                triggerUpdate(key);
            }
        }
        return;
    }
    const batch = writeBatch(db);
    const ref1 = doc(db, "users", userId, "categories", cat1.id);
    const ref2 = doc(db, "users", userId, "categories", cat2.id);
    batch.set(ref1, sanitizeForFirestore({ ...cat1, order: o2 }), { merge: true });
    batch.set(ref2, sanitizeForFirestore({ ...cat2, order: o1 }), { merge: true });
    await batch.commit();
};

export const deleteCategoryFromCloud = async (userId: string, categoryId: string) => {
    if (isOffline(userId)) {
        const key = `categories_${userId}`;
        const stored = localStorage.getItem(key);
        if (stored) {
            let items: Category[] = JSON.parse(stored);
            items = items.filter(c => c.id !== categoryId);
            localStorage.setItem(key, JSON.stringify(items));
            triggerUpdate(key);
        }
        return;
    }
    await deleteDoc(doc(db, "users", userId, "categories", categoryId));
};

export const subscribeToChatSessions = (userId: string, callback: (sessions: ChatSession[]) => void) => {
    if (isOffline(userId)) {
        const key = `chats_${userId}`;
        const load = () => {
            const stored = localStorage.getItem(key);
            callback(stored ? JSON.parse(stored) : []);
        };
        load();
        const handler = () => load();
        window.addEventListener(`local-db-update-${key}`, handler);
        return () => window.removeEventListener(`local-db-update-${key}`, handler);
    }
    const q = query(collection(db, "users", userId, "chat_sessions"), orderBy("updatedAt", "desc"));
    return onSnapshot(q, (snapshot) => {
        const sessions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ChatSession));
        callback(sessions);
    }, (error) => {
        handleFirestoreError(error, OperationType.LIST, `users/${userId}/chat_sessions`);
        callback([]);
    });
};

export const createChatSession = async (userId: string, firstMessage: string): Promise<string> => {
    const sessionId = Date.now().toString();
    const newSession: ChatSession = {
        id: sessionId,
        userId,
        title: firstMessage.substring(0, 30) + (firstMessage.length > 30 ? "..." : ""),
        historial: [],
        createdAt: Date.now(),
        updatedAt: Date.now()
    };
    if (isOffline(userId)) {
        const key = `chats_${userId}`;
        const stored = localStorage.getItem(key);
        const sessions: ChatSession[] = stored ? JSON.parse(stored) : [];
        sessions.unshift(newSession);
        localStorage.setItem(key, JSON.stringify(sessions));
        triggerUpdate(key);
        return sessionId;
    }
    await setDoc(doc(db, "users", userId, "chat_sessions", sessionId), sanitizeForFirestore(newSession));
    return sessionId;
};

export const updateChatSessionTitle = async (userId: string, sessionId: string, newTitle: string) => {
    if (isOffline(userId)) {
        const key = `chats_${userId}`;
        const stored = localStorage.getItem(key);
        if (stored) {
            const sessions: ChatSession[] = JSON.parse(stored);
            const idx = sessions.findIndex(s => s.id === sessionId);
            if (idx >= 0) {
                sessions[idx].title = newTitle;
                localStorage.setItem(key, JSON.stringify(sessions));
                triggerUpdate(key);
            }
        }
        return;
    }
    const ref = doc(db, "users", userId, "chat_sessions", sessionId);
    await updateDoc(ref, sanitizeForFirestore({ title: newTitle }));
};

export const updateChatHistory = async (userId: string, sessionId: string, history: GeminiContent[]) => {
    if (isOffline(userId)) {
        const key = `chats_${userId}`;
        const stored = localStorage.getItem(key);
        if (stored) {
            const sessions: ChatSession[] = JSON.parse(stored);
            const idx = sessions.findIndex(s => s.id === sessionId);
            if (idx >= 0) {
                sessions[idx].historial = history;
                sessions[idx].updatedAt = Date.now();
                localStorage.setItem(key, JSON.stringify(sessions));
                triggerUpdate(key);
            }
        }
        return;
    }
    const ref = doc(db, "users", userId, "chat_sessions", sessionId);
    const cleanedHistory = sanitizeForFirestore(history);
    await updateDoc(ref, {
        historial: cleanedHistory,
        updatedAt: Date.now()
    });
};

export const deleteChatSession = async (userId: string, sessionId: string) => {
     if (isOffline(userId)) {
        const key = `chats_${userId}`;
        const stored = localStorage.getItem(key);
        if (stored) {
            let sessions: ChatSession[] = JSON.parse(stored);
            sessions = sessions.filter(s => s.id !== sessionId);
            localStorage.setItem(key, JSON.stringify(sessions));
            triggerUpdate(key);
        }
        return;
     }
     await deleteDoc(doc(db, "users", userId, "chat_sessions", sessionId));
};

export const getChatHistory = async (userId: string, sessionId: string): Promise<GeminiContent[]> => {
    if (isOffline(userId)) {
        const key = `chats_${userId}`;
        const stored = localStorage.getItem(key);
        if (stored) {
             const sessions: ChatSession[] = JSON.parse(stored);
             const s = sessions.find(sess => sess.id === sessionId);
             return s ? s.historial : [];
        }
        return [];
    }
    const ref = doc(db, "users", userId, "chat_sessions", sessionId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
        return (snap.data() as ChatSession).historial;
    }
    return [];
};

export const subscribeToChatSummaries = (userId: string, callback: (summaries: ChatSummary[]) => void) => {
    if (isOffline(userId)) {
        const key = `chat_summaries_${userId}`;
        const load = () => {
            const stored = localStorage.getItem(key);
            callback(stored ? JSON.parse(stored) : []);
        };
        load();
        const handler = () => load();
        window.addEventListener(`local-db-update-${key}`, handler);
        return () => window.removeEventListener(`local-db-update-${key}`, handler);
    }
    const q = query(collection(db, "users", userId, "chat_summaries"), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snapshot) => {
        const summaries = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ChatSummary));
        callback(summaries);
    }, (error) => {
        handleFirestoreError(error, OperationType.LIST, `users/${userId}/chat_summaries`);
        callback([]);
    });
};

export const saveChatSummary = async (userId: string, summary: ChatSummary) => {
    if (isOffline(userId)) {
        const key = `chat_summaries_${userId}`;
        const stored = localStorage.getItem(key);
        const summaries: ChatSummary[] = stored ? JSON.parse(stored) : [];
        summaries.unshift(summary);
        localStorage.setItem(key, JSON.stringify(summaries));
        triggerUpdate(key);
        return;
    }
    await setDoc(doc(db, "users", userId, "chat_summaries", summary.id), sanitizeForFirestore(summary));
};

export const deleteChatSummary = async (userId: string, summaryId: string) => {
    if (isOffline(userId)) {
        const key = `chat_summaries_${userId}`;
        const stored = localStorage.getItem(key);
        if (stored) {
            let summaries: ChatSummary[] = JSON.parse(stored);
            summaries = summaries.filter(s => s.id !== summaryId);
            localStorage.setItem(key, JSON.stringify(summaries));
            triggerUpdate(key);
        }
        return;
    }
    await deleteDoc(doc(db, "users", userId, "chat_summaries", summaryId));
};

export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
    if (!db || isOffline(userId)) {
        const key = `profile_${userId}`;
        const stored = localStorage.getItem(key);
        return stored ? JSON.parse(stored) : null;
    }
    try {
        const profileRef = doc(db, "users", userId, "profile", "main");
        const snap = await getDoc(profileRef);
        if (snap.exists()) {
            return { uid: snap.id, ...snap.data() } as UserProfile;
        }
        const topProfileRef = doc(db, "profiles", userId);
        const topSnap = await getDoc(topProfileRef);
        if (topSnap.exists()) {
            return { uid: topSnap.id, ...topSnap.data() } as UserProfile;
        }
        return null;
    } catch (e) {
        console.warn("getUserProfile error:", e);
        return null;
    }
};

export const subscribeToUserProfile = (userId: string, callback: (profile: UserProfile | null) => void) => {
    if (isOffline(userId)) {
        const key = `profile_${userId}`;
        const load = () => {
             const stored = localStorage.getItem(key);
             callback(stored ? JSON.parse(stored) : null);
        };
        load();
        const handler = () => load();
        window.addEventListener(`local-db-update-${key}`, handler);
        return () => window.removeEventListener(`local-db-update-${key}`, handler);
    }
    const profileRef = doc(db, "users", userId, "profile", "main");
    const userRef = doc(db, "users", userId);
    let hasSyncedPublicProjection = false;

    return onSnapshot(profileRef, async (snap) => {
        if (snap.exists()) {
            const data = snap.data() as UserProfile;
            callback({ uid: snap.id, ...data });

            // Auto-sincronización de seguridad de profile/public — SOLO UNA VEZ por suscripción,
            // para no generar una escritura cada vez que cambia el perfil (eso amplificaba las
            // lecturas de cualquier otro panel, como Gestión, que observa ese mismo grupo de documentos).
            if (!hasSyncedPublicProjection) {
                hasSyncedPublicProjection = true;
                try {
                    const publicRef = doc(db, "users", userId, "profile", "public");
                    const publicData = buildPublicProfileData(data, userId);
                    setDoc(publicRef, sanitizeForFirestore(publicData), { merge: true }).catch(() => {});
                } catch {
                    // Silencioso
                }
            }
        } else {
            // FALLBACK / RESCATE: Si no existe en profile/main, buscar en la raíz (compatibilidad v1)
            try {
                const rootSnap = await getDoc(userRef);
                const rootData = rootSnap.data();
                if (rootData && rootData.role) { // Validar que parece un perfil
                    console.log("[subscribeToUserProfile] Migrando perfil detectado en raíz a subcolección...");
                    // Auto-sanación: Guardar en el lugar correcto. El uid es userId.
                    const publicRef = doc(db, "users", userId, "profile", "public");
                    const publicData = buildPublicProfileData(rootData, userId);
                    const batch = writeBatch(db);
                    batch.set(profileRef, { ...rootData, uid: userId }, { merge: true });
                    batch.set(publicRef, sanitizeForFirestore(publicData), { merge: true });
                    await batch.commit();
                    // No llamamos al callback aquí: esperamos al siguiente snapshot de profileRef
                } else {
                    callback(null);
                }
            } catch (e) {
                console.warn("Error en rescate de perfil:", e);
                callback(null);
            }
        }
    }, (error) => {
        handleFirestoreError(error, OperationType.GET, `users/${userId}/profile/main`);
        callback(null);
    });
};

export const PUBLIC_PROFILE_FIELDS = [
    'displayName',
    'photoURL',
    'mainSpecialty',
    'specialties',
    'status',
    'role',
    'residentYear',
    'experienceYears',
    'workplace',
    'isVerified',
    'lastSeenAt'
] as const;

export const buildPublicProfileData = (source: Partial<UserProfile>, uid: string) => {
    const publicData: Record<string, any> = {
        uid,
        isPublic: true
    };
    for (const key of PUBLIC_PROFILE_FIELDS) {
        if (source[key] !== undefined) {
            publicData[key] = source[key];
        }
    }
    return publicData;
};

export const saveUserProfile = async (profile: UserProfile) => {
    if (isOffline(profile.uid)) {
        const key = `profile_${profile.uid}`;
        localStorage.setItem(key, JSON.stringify(profile));
        triggerUpdate(key);
        return;
    }
    try {
        const profileRef = doc(db, "users", profile.uid, "profile", "main");
        const publicRef = doc(db, "users", profile.uid, "profile", "public");
        const userRef = doc(db, "users", profile.uid);

        const publicData = buildPublicProfileData(profile, profile.uid);

        const batch = writeBatch(db);
        batch.set(profileRef, sanitizeForFirestore(profile), { merge: true });
        batch.set(publicRef, sanitizeForFirestore(publicData), { merge: true });
        // Mantener también actualización en raíz para triggers de presencia
        batch.set(userRef, { lastSeenAt: Date.now() }, { merge: true });
        await batch.commit();
    } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, `users/${profile.uid}/profile/main`);
    }
};

export const updateUserProfile = async (uid: string, data: Partial<UserProfile>) => {
    if (isOffline(uid)) {
        const key = `profile_${uid}`;
        const existing = localStorage.getItem(key);
        if (existing) {
            const profile = JSON.parse(existing);
            localStorage.setItem(key, JSON.stringify({ ...profile, ...data }));
            triggerUpdate(key);
        }
        return;
    }
    
    const sanitizedData = sanitizeForFirestore({ ...data, updatedAt: Date.now() });
    const profileRef = doc(db, "users", uid, "profile", "main");
    const publicRef = doc(db, "users", uid, "profile", "public");
    const userRef = doc(db, "users", uid);
    
    const publicData = buildPublicProfileData(data, uid);

    try {
        const batch = writeBatch(db);
        batch.set(profileRef, sanitizedData, { merge: true });
        batch.set(publicRef, sanitizeForFirestore(publicData), { merge: true });
        batch.set(userRef, { lastSeenAt: Date.now() }, { merge: true });
        await batch.commit();
    } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, `users/${uid}/profile/main`);
    }
};

export const deleteUserProfileCompletely = async (uid: string) => {
    if (isOffline(uid)) {
        localStorage.removeItem(`profile_${uid}`);
        triggerUpdate(`profile_${uid}`);
        return;
    }
    const profileRef = doc(db, "users", uid, "profile", "main");
    const publicRef = doc(db, "users", uid, "profile", "public");
    const userRef = doc(db, "users", uid);
    try {
        const batch = writeBatch(db);
        batch.delete(profileRef);
        batch.delete(publicRef);
        batch.delete(userRef);
        await batch.commit();
    } catch (error) {
        handleFirestoreError(error, OperationType.DELETE, `users/${uid}/profile/main`);
        throw error;
    }
};

export const togglePinContact = async (userId: string, contactId: string, isPinned: boolean) => {
    if (isOffline(userId)) return;
    const ref = doc(db, "users", userId);
    await updateDoc(ref, sanitizeForFirestore({
        pinnedContacts: isPinned ? arrayUnion(contactId) : arrayRemove(contactId)
    }));
};

/**
 * DIRECTORIO: Carga optimizada (Real-time).
 * Lee exclusivamente desde users/{uid}/profile/public mediante collectionGroup filtrado con isPublic == true.
 * Garantiza cumplimiento HIPAA/GDPR eliminando datos sensibles (DNI, localización, informes de auditoría).
 */
export const subscribeToDirectory = (arg1: any, arg2?: any) => {
    if (!db) return () => {};
    const callback = typeof arg1 === 'function' ? arg1 : arg2;
    if (typeof callback !== 'function') return () => {};

    try {
        const q = query(
            collectionGroup(db, "profile"),
            where("isPublic", "==", true),
            limit(200)
        );
        return onSnapshot(q, (snapshot) => {
            const users = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    uid: data.uid || doc.ref.parent.parent?.id || doc.id,
                    ...data
                } as UserProfile;
            });
            callback(users, null); // Clear error on success
        }, (error) => {
            console.error("Error subscribing to public directory (collectionGroup):", error);
            
            const msg = error.message.toLowerCase();
            // FALLBACK: Si no hay índice o permisos, intentar carga manual basada en escaneo
            if (msg.includes('index') || msg.includes('permission') || msg.includes('insufficient')) {
                fetchDirectoryFallbackLegacy((users, fallbackError) => {
                    // Si el fallback encuentra usuarios, los enviamos y limpiamos el error principal
                    if (users.length > 0) {
                        callback(users, null);
                    } else {
                        // Si falla también el fallback, reportamos el error original
                        callback([], error.message);
                    }
                });
            } else {
                callback([], error.message);
            }
        });
    } catch (e: any) {
        console.error("Critical Directory Error:", e);
        callback([], e.message);
        return () => {};
    }
};

/**
 * DIRECTORIO MASTER / ADMIN:
 * Carga privilegiada de perfiles completos (profile/main) para el Centro de Mando del Master.
 * Solo accesible por cuentas con rol ADMIN o RADIOLOGY MASTER.
 */
export const subscribeToAdminDirectory = (callback: (users: UserProfile[], error: string | null) => void) => {
    if (!db) return () => {};

    let activeUnsub: () => void = () => {};
    let isCancelled = false;

    let hasSyncedPublicProjections = false;

    const startPrivileged = () => {
        try {
            const q = query(collectionGroup(db, "profile"), limit(300));
            activeUnsub = onSnapshot(q, (snapshot) => {
                if (isCancelled) return;
                const profileMap = new Map<string, UserProfile>();

                // Prioridad a profile/main para campos privados, con fallback a profile/public
                snapshot.docs.forEach(docSnap => {
                    const uid = docSnap.ref.parent.parent?.id || docSnap.id;
                    const data = docSnap.data() as UserProfile;
                    if (docSnap.id === 'main') {
                        profileMap.set(uid, { uid, ...data });
                    } else if (!profileMap.has(uid)) {
                        profileMap.set(uid, { uid, ...data });
                    }
                });

                const profiles = Array.from(profileMap.values());

                // Sincronización silenciosa en segundo plano para asegurar que los perfiles
                // existentes tengan su proyección pública generada en profile/public.
                // Se ejecuta SOLO UNA VEZ por sesión (no en cada snapshot) para no generar
                // un bucle infinito de escritura->snapshot->escritura, y para no "resucitar"
                // perfiles que acaban de ser eliminados intencionalmente.
                if (!hasSyncedPublicProjections) {
                    hasSyncedPublicProjections = true;
                    try {
                        const batch = writeBatch(db);
                        let count = 0;
                        for (const u of profiles) {
                            if (u.uid && u.role !== 'ADMIN' && u.role !== 'RADIOLOGY MASTER') {
                                const pubRef = doc(db, "users", u.uid, "profile", "public");
                                const pubData = buildPublicProfileData(u, u.uid);
                                batch.set(pubRef, sanitizeForFirestore(pubData), { merge: true });
                                count++;
                                if (count >= 100) break;
                            }
                        }
                        if (count > 0) {
                            batch.commit().catch(() => {});
                        }
                    } catch (syncErr) {
                        console.warn("[AdminDirectory] Public sync notice:", syncErr);
                    }
                }

                callback(profiles, null);
            }, (error) => {
                if (isCancelled) return;
                console.warn("[subscribeToAdminDirectory] Privileged query denied or failed, attempting graceful fallback:", error.message);
                startFallback(error.message);
            });
        } catch (e: any) {
            if (isCancelled) return;
            console.warn("[subscribeToAdminDirectory] Privileged query init failed, attempting fallback:", e.message);
            startFallback(e.message);
        }
    };

    const startFallback = (rootReason: string) => {
        try {
            // Nivel 1 Fallback: Proyección pública comunitaria (permitida para todo usuario verificado/admin)
            const fallbackQ = query(collectionGroup(db, "profile"), where("isPublic", "==", true), limit(300));
            activeUnsub = onSnapshot(fallbackQ, async (pubSnap) => {
                if (isCancelled) return;
                const fallbackMap = new Map<string, UserProfile>();
                pubSnap.docs.forEach(d => {
                    const uid = d.ref.parent.parent?.id || d.id;
                    fallbackMap.set(uid, { uid, ...d.data() } as UserProfile);
                });

                // Nivel 2 Fallback: Barrido de rescate en colección raíz /users
                try {
                    const usersSnap = await getDocs(query(collection(db, "users"), limit(100)));
                    for (const uDoc of usersSnap.docs) {
                        if (!fallbackMap.has(uDoc.id)) {
                            const uData = uDoc.data();
                            fallbackMap.set(uDoc.id, {
                                uid: uDoc.id,
                                displayName: uData.displayName || uData.name || uDoc.id,
                                email: uData.email || '',
                                role: uData.role || 'MÉDICO RADIÓLOGO',
                                isVerified: uData.isVerified || false,
                                ...uData
                            } as UserProfile);
                        }
                    }
                } catch (scanErr) {
                    console.debug("[subscribeToAdminDirectory] Fallback /users scan notice:", scanErr);
                }

                const fallbackList = Array.from(fallbackMap.values());
                console.log(`[ManagementScreen] Admin directory received: ${fallbackList.length} users found. (Degradación elegante activa)`);
                callback(
                    fallbackList,
                    `MODO RESTRINGIDO: Operando mediante proyección pública y rescate de directorio (${rootReason}).`
                );
            }, (pubErr) => {
                if (isCancelled) return;
                console.error("[subscribeToAdminDirectory] Fallback query error:", pubErr);
                // Nivel 3 Fallback: Intento directo a colección /users
                getDocs(query(collection(db, "users"), limit(100))).then(uSnap => {
                    if (isCancelled) return;
                    const uList = uSnap.docs.map(d => ({ uid: d.id, ...d.data() } as UserProfile));
                    callback(uList, `MODO RESTRINGIDO: Acceso directo raíz (${pubErr.message}).`);
                }).catch(finalErr => {
                    if (isCancelled) return;
                    callback([], `ERROR DE ACCESO AL DIRECTORIO: ${finalErr.message}`);
                });
            });
        } catch (fbErr: any) {
            callback([], `ERROR CRÍTICO: ${fbErr.message}`);
        }
    };

    startPrivileged();

    return () => {
        isCancelled = true;
        activeUnsub();
    };
};

/**
 * Consulta puntual privilegiada de un perfil completo (profile/main).
 * Usado para auditoría clínica en el panel del Master.
 */
export const fetchFullUserProfile = async (uid: string): Promise<UserProfile | null> => {
    if (!db || !uid) return null;
    try {
        const profileRef = doc(db, "users", uid, "profile", "main");
        const snap = await getDoc(profileRef);
        if (snap.exists()) {
            return { uid, ...snap.data() } as UserProfile;
        }
        return null;
    } catch (e) {
        console.error("Error fetching full user profile for admin:", e);
        return null;
    }
};


/**
 * Fallback final usando listado de usuarios (lento pero seguro si hay documentos raíz).
 */
const fetchDirectoryFallbackLegacy = async (callback: (users: UserProfile[], error?: string) => void) => {
    console.log("[fetchDirectoryFallbackLegacy] Starting legacy scan...");
    try {
        const foundUids = new Set<string>();
        const foundProfiles: UserProfile[] = [];

        // 1. Escaneo de 'users' - SOLO si somos admin (para evitar errores 403 innecesarios)
        try {
            // Intentar solo si creemos que somos admin
            const usersSnap = await getDocs(query(collection(db, "users"), limit(50)));
            usersSnap.docs.forEach(d => foundUids.add(d.id));
        } catch (e: any) { 
            console.warn("[fetchDirectoryFallbackLegacy] 'users' scan bypassed or failed:", e.message);
        }

        // 2. Rescate de 'peer_chats' - Muy útil para encontrar con quién ha hablado el usuario
        try {
            const chatsSnap = await getDocs(query(collection(db, "peer_chats"), limit(100)));
            chatsSnap.docs.forEach(d => {
                const data = d.data();
                if (data.participants && Array.isArray(data.participants)) {
                    data.participants.forEach((p: string) => foundUids.add(p));
                }
            });
        } catch (e: any) { 
            console.warn("[fetchDirectoryFallbackLegacy] 'peer_chats' rescue failed:", e.message);
        }

        // 3. Rescate de 'workGroups'
        try {
            const groupsSnap = await getDocs(query(collection(db, "workGroups"), limit(50)));
            groupsSnap.docs.forEach(d => {
                const data = d.data();
                if (data.members && Array.isArray(data.members)) {
                    data.members.forEach((m: string) => foundUids.add(m));
                }
            });
        } catch (e: any) { 
            console.warn("[fetchDirectoryFallbackLegacy] 'workGroups' rescue failed:", e.message);
        }

        console.log("[fetchDirectoryFallbackLegacy] Total potential UIDs found:", foundUids.size);
        
        if (foundUids.size === 0) {
            // No borramos datos si ya hay algo en caché
            return;
        }
        
        // Cargar perfiles uno a uno (esto siempre funciona si el documento existe)
        const uidsToFetch = Array.from(foundUids).slice(0, 50); // Limitar para no saturar
        for (const uid of uidsToFetch) {
            try {
                const s = await getDoc(doc(db, "users", uid, "profile", "main"));
                if (s.exists()) {
                    const p = s.data() as UserProfile;
                    foundProfiles.push(p);
                    // Materializar
                    saveUserProfile(p).catch(() => {});
                }
            } catch (e) {
                console.warn(`Failed to fetch profile for UID: ${uid}`, e);
            }
        }

        if (foundProfiles.length > 0) {
            callback(foundProfiles, null);
        }
    } catch (err: any) {
        console.error("Directory Final Fallback Error:", err);
    }
};

export const subscribeToActiveChats = (userId: string, callback: (chats: PeerChat[]) => void) => {
    if (isOffline(userId)) {
        callback([]);
        return () => {};
    }
    // Optimization: Remove orderBy to avoid requiring a composite index which often causes "Permissions" errors
    const q = query(
        collection(db, "peer_chats"),
        where("participants", "array-contains", userId)
    );
    return onSnapshot(q, (snapshot) => {
        const chats = snapshot.docs
            .map(doc => doc.data() as PeerChat)
            .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
        callback(chats);
    }, (error) => {
        handleFirestoreError(error, OperationType.LIST, "peer_chats");
        callback([]);
    });
};

export const subscribeToPeerChat = (chatId: string, callback: (messages: PeerMessage[]) => void) => {
    if (!db) {
        callback([]);
        return () => {};
    }
    const q = query(collection(db, "peer_chats", chatId, "messages"), orderBy("timestamp", "asc"));
  return onSnapshot(q, (snapshot) => {
    const msgs = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as PeerMessage));
    callback(msgs);
  }, (error) => {
    console.error("Error subscribing to peer chat:", error);
    callback([]);
  });
};

export const sendPeerMessage = async (chatId: string, message: PeerMessage) => {
    if (!db) return;
    // CRITICAL FIX: Robust sanitization before Firestore call
    const cleanMessage = sanitizeForFirestore(message);
    const ref = doc(db, "peer_chats", chatId, "messages", cleanMessage.id);
    await setDoc(ref, cleanMessage);
    await setDoc(doc(db, "peer_chats", chatId), sanitizeForFirestore({
        id: chatId,
        participants: chatId.split('_'),
        lastMessage: cleanMessage,
        updatedAt: cleanMessage.timestamp
    }), { merge: true });
};

export const subscribeToPublicWorkGroups = (callback: (groups: WorkGroup[]) => void) => {
    if (!db) { callback([]); return () => {}; }
    const q = query(
        collection(db, "workGroups"),
        where("isPublic", "==", true),
        limit(20)
    );
    return onSnapshot(q, (snapshot) => {
        const groups = snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() } as WorkGroup))
            .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
        callback(groups);
    }, (error) => {
        console.warn("Error subscribing to public groups:", error);
        callback([]);
    });
};

export const sendGroupJoinRequest = async (request: Omit<GroupJoinRequest, 'id' | 'createdAt'>) => {
    if (!db) return;
    const cleanRequest = sanitizeForFirestore({
        ...request,
        createdAt: Date.now()
    });
    // Use subcollection pattern matching firestore.rules: workGroups/{groupId}/requests/{userId}
    const ref = doc(db, "workGroups", request.groupId, "requests", request.userId);
    await setDoc(ref, cleanRequest);
};

export const subscribeToGroupRequests = (groupId: string, callback: (requests: GroupJoinRequest[]) => void) => {
    if (!db) { callback([]); return () => {}; }
    const q = query(
        collection(db, "workGroups", groupId, "requests"),
        where("status", "==", "PENDING"),
        orderBy("createdAt", "desc")
    );
    return onSnapshot(q, (snapshot) => {
        const requests = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as GroupJoinRequest));
        callback(requests);
    }, (error) => {
        console.error(`Error subscribing to requests for group ${groupId}:`, error);
        callback([]);
    });
};

export const updateGroupJoinRequest = async (groupId: string, userId: string, status: 'ACCEPTED' | 'REJECTED') => {
    if (!db) return;
    const requestRef = doc(db, "workGroups", groupId, "requests", userId);
    
    try {
        if (status === 'ACCEPTED') {
            const requestSnap = await getDoc(requestRef);
            if (requestSnap.exists()) {
                const data = requestSnap.data() as GroupJoinRequest;
                // Add to members
                await addMemberToGroup(groupId, userId, data.role || 'MEMBER');
            }
        }
        
        await updateDoc(requestRef, { 
            status,
            resolvedAt: Date.now()
        });
    } catch (error) {
        handleFirestoreError(error, OperationType.UPDATE, `workGroups/${groupId}/requests/${userId}`);
    }
};

export const testFirestoreConnection = async (userId: string): Promise<{ success: boolean; message: string }> => {
    if (!db) return { success: false, message: "Base de datos no inicializada" };
    if (isOffline(userId)) return { success: false, message: "Modo Offline activado" };
    
    try {
        const testRef = doc(db, "connectivity_test", "ping");
        await getDocFromServer(testRef);
        return { success: true, message: "Cloud Online" };
    } catch (e: any) {
        if (e.code === 'unavailable' || e.message?.includes('offline') || e.message?.includes('insufficient permissions')) {
            // Insufficient permissions usually means it's connected but ruled out, 
            // but for a connection test we treat it as cloud-reachable if it didn't timeout
            return { success: true, message: "Cloud Online" };
        }
        return { success: false, message: "Sin conexión a Cloud - Modo Local" };
    }
};

export const createWorkGroup = async (group: Omit<WorkGroup, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!db) return;
    const cleanGroup = sanitizeForFirestore({
        name: group.name,
        description: group.description,
        admins: group.admins,
        isPublic: group.isPublic !== undefined ? group.isPublic : true,
        createdBy: group.createdBy,
        createdAt: Date.now(),
        updatedAt: Date.now()
    });
    const ref = await addDoc(collection(db, "workGroups"), cleanGroup);
    
    // Add creator as member in subcollection
    await setDoc(doc(db, "workGroups", ref.id, "members", group.createdBy), {
        userId: group.createdBy,
        role: 'ADMIN',
        joinedAt: Date.now(),
        groupAdminId: group.createdBy // Store admin ID for secure rules
    });
    
    return ref.id;
};

export const subscribeToMyGroupRequests = (userId: string, callback: (requests: GroupJoinRequest[]) => void) => {
    if (!db) { callback([]); return () => {}; }
    // Now use collectionGroup because requests are subcollections of workGroups
    const q = query(
        collectionGroup(db, "requests"),
        where("userId", "==", userId)
    );
    return onSnapshot(q, (snapshot) => {
        const requests = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as GroupJoinRequest));
        callback(requests);
    }, (error) => {
        console.error("Error subscribing to my group requests:", error);
        callback([]);
    });
};

export const handleGroupJoinRequest = async (groupId: string, userId: string, status: 'ACCEPTED' | 'REJECTED' | 'CANCELLED') => {
    if (!db) return;
    
    const requestRef = doc(db, "workGroups", groupId, "requests", userId);
    
    if (status === 'ACCEPTED') {
        const batch = writeBatch(db);
        const memberRef = doc(db, "workGroups", groupId, "members", userId);
        const groupRef = doc(db, "workGroups", groupId);
        
        batch.update(requestRef, { status: 'ACCEPTED', processedAt: Date.now() });
        batch.set(memberRef, {
            userId,
            role: 'MEMBER',
            joinedAt: Date.now(),
            groupAdminId: auth.currentUser?.uid || '' // Current user (admin) who accepted
        });
        batch.update(groupRef, { updatedAt: Date.now() });
        
        await batch.commit();
    } else {
        await updateDoc(requestRef, { 
            status: status === 'REJECTED' ? 'REJECTED' : 'CANCELLED',
            processedAt: Date.now() 
        });
    }
};

export const proposeAdminTransfer = async (groupId: string, newAdminId: string) => {
    if (!db) return;
    const ref = doc(db, "workGroups", groupId);
    await updateDoc(ref, { 
        pendingAdminId: newAdminId,
        updatedAt: Date.now()
    });
};

export const acceptAdminRole = async (groupId: string, userId: string) => {
    if (!db) return;
    const ref = doc(db, "workGroups", groupId);
    await updateDoc(ref, { 
        admins: [userId], // El usuario dijo "solo un administrador por grupo"
        pendingAdminId: null,
        updatedAt: Date.now()
    });
};

export const leaveWorkGroup = async (groupId: string, userId: string) => {
    if (!db) return;
    const batch = writeBatch(db);
    const memberRef = doc(db, "workGroups", groupId, "members", userId);
    const groupRef = doc(db, "workGroups", groupId);
    
    batch.delete(memberRef);
    batch.update(groupRef, { 
        admins: arrayRemove(userId),
        updatedAt: Date.now()
    });
    
    await batch.commit();
};

export const addMemberToGroup = async (groupId: string, userId: string, role: string = 'MEMBER', groupAdminId: string = '') => {
    if (!db) return;
    const memberRef = doc(db, "workGroups", groupId, "members", userId);
    await setDoc(memberRef, {
        userId,
        role,
        joinedAt: Date.now(),
        groupAdminId: groupAdminId || auth.currentUser?.uid || ''
    });
};

export const updateWorkGroup = async (groupId: string, data: Partial<WorkGroup>) => {
    if (!db) return;
    const ref = doc(db, "workGroups", groupId);
    await updateDoc(ref, sanitizeForFirestore({
        ...data,
        updatedAt: Date.now()
    }));
};

export const sendWorkGroupMessage = async (groupId: string, message: Omit<WorkGroupMessage, 'id' | 'timestamp'>) => {
    if (!db) return;
    const timestamp = Date.now();
    const msgRef = await addDoc(collection(db, "workGroups", groupId, "messages"), sanitizeForFirestore({
        ...message,
        timestamp
    }));
    
    const groupRef = doc(db, "workGroups", groupId);
    await updateDoc(groupRef, sanitizeForFirestore({
        lastMessage: {
            id: msgRef.id,
            ...message,
            timestamp
        },
        updatedAt: timestamp
    }));
};

export const subscribeToWorkGroups = (userId: string, callback: (groups: WorkGroup[]) => void) => {
    // DESACTIVADO TEMPORALMENTE POR ALTA DEMANDA Y ERRORES DE PERMISOS
    // callback([]);
    return () => {};
/*
    if (!db || isOffline(userId)) { callback([]); return () => {}; }
    
    // Use collectionGroup to find all memberships for the user
    const qGroup = query(
        collectionGroup(db, "members"),
        where("userId", "==", userId)
    );
    
    const groupUnsubs: Record<string, () => void> = {};
    const groupsData: Record<string, WorkGroup> = {};

    return onSnapshot(qGroup, (snapshot) => {
        const currentGroupIds = new Set(
            snapshot.docs
                .map(d => d.ref.parent.parent?.id)
                .filter(id => id !== undefined) as string[]
        );
        
        // Remove listeners for groups I'm no longer in
        Object.keys(groupUnsubs).forEach(id => {
            if (!currentGroupIds.has(id)) {
                groupUnsubs[id]?.();
                delete groupUnsubs[id];
                delete groupsData[id];
            }
        });

        // If no groups, emit empty array
        if (currentGroupIds.size === 0) {
            callback([]);
        }

        // Add listeners for new groups
        currentGroupIds.forEach(groupId => {
            if (!groupUnsubs[groupId]) {
                const groupDocRef = doc(db, "workGroups", groupId);
                groupUnsubs[groupId] = onSnapshot(groupDocRef, (docSnap) => {
                    if (docSnap.exists()) {
                        groupsData[groupId] = { 
                            id: docSnap.id, 
                            ...docSnap.data() 
                        } as WorkGroup;
                        
                        // Emit all current data for joined groups, sorted by updatedAt
                        const sortedGroups = Object.values(groupsData).sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
                        callback(sortedGroups);
                    }
                }, (error) => {
                    console.warn(`Error subscribing to group doc ${groupId}:`, error);
                });
            }
        });
    }, (error) => {
        handleFirestoreError(error, OperationType.LIST, "workGroups_members_group");
        callback([]);
    });
*/
};

export const subscribeToWorkGroupMessages = (groupId: string, callback: (messages: WorkGroupMessage[]) => void) => {
    if (!db) { callback([]); return () => {}; }
    const q = query(collection(db, "workGroups", groupId, "messages"), orderBy("timestamp", "asc"));
    return onSnapshot(q, (snapshot) => {
        const messages = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as WorkGroupMessage));
        callback(messages);
    }, (error) => {
        console.error("Error subscribing to group messages:", error);
        callback([]);
    });
};

export const saveDraftingState = async (userId: string, state: { text: string, senderId: string }) => {
    if (isOffline(userId)) return;
    try {
        const ref = doc(db, "users", userId, "state", "drafting");
        await setDoc(ref, { ...state, updatedAt: Date.now() }, { merge: true });
    } catch (e) {
        console.warn("Error saving drafting state:", e);
    }
};

export const subscribeToDraftingState = (userId: string, callback: (state: { text: string, senderId: string }) => void) => {
    if (isOffline(userId)) return () => {};
    const ref = doc(db, "users", userId, "state", "drafting");
    return onSnapshot(ref, (doc) => {
        if (doc.exists()) {
            callback(doc.data() as { text: string, senderId: string });
        }
    }, (error) => {
        console.warn("Error subscribing to drafting state:", error);
    });
};

/**
 * Persistencia de versiones de sesión para módulos (Modular-1, Redacción PhD)
 * Permite que la navegación < n > sea persistente y homogénea.
 */
export const saveModuleSectionHistory = async (userId: string, moduleId: string, sectionId: string, content: string) => {
  if (!userId || userId === 'guest_dev_offline') return;
  try {
    const docRef = doc(db, 'module_sessions', `${userId}_${moduleId}`);
    await setDoc(docRef, {
      [`histories.${sectionId}`]: arrayUnion(content),
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (e) {
    handleFirestoreError(e, OperationType.WRITE, `module_sessions/${userId}_${moduleId}`);
  }
};

export const clearModuleSessions = async (userId: string, moduleId: string) => {
  if (!userId || userId === 'guest_dev_offline') return;
  try {
    const docRef = doc(db, 'module_sessions', `${userId}_${moduleId}`);
    await deleteDoc(docRef);
  } catch (e) {
    handleFirestoreError(e, OperationType.DELETE, `module_sessions/${userId}_${moduleId}`);
  }
};

export const subscribeToModuleSession = (userId: string, moduleId: string, callback: (data: any) => void) => {
  if (!userId || userId === 'guest_dev_offline') return () => {};
  const docRef = doc(db, 'module_sessions', `${userId}_${moduleId}`);
  return onSnapshot(docRef, (snap) => {
    if (snap.exists()) {
      callback(snap.data());
    } else {
      callback(null);
    }
  }, (error) => {
    handleFirestoreError(error, OperationType.GET, `module_sessions/${userId}_${moduleId}`);
    callback(null);
  });
};

/**
 * Historial de informes generados para Redacción PhD
 * Almacena hasta 15 snapshots completos (texto, configuración, resultados)
 */
export const saveDraftingGenerationSnapshot = async (userId: string, snapshot: any) => {
  if (!userId || userId === 'guest_dev_offline') return;
  try {
    const docRef = doc(db, 'drafting_generation_log', userId);
    const snap = await getDoc(docRef);
    const current = snap.exists() ? (snap.data().entries || []) : [];
    const next = [...current, snapshot].slice(-15);
    await setDoc(docRef, { entries: next, updatedAt: serverTimestamp() }, { merge: true });
  } catch (e) {
    handleFirestoreError(e, OperationType.WRITE, `drafting_generation_log/${userId}`);
  }
};

export const subscribeToDraftingGenerationLog = (userId: string, callback: (entries: any[]) => void) => {
  if (!userId || userId === 'guest_dev_offline') return () => {};
  const docRef = doc(db, 'drafting_generation_log', userId);
  return onSnapshot(docRef, (snap) => {
    callback(snap.exists() ? (snap.data().entries || []) : []);
  }, (error) => {
    handleFirestoreError(error, OperationType.GET, `drafting_generation_log/${userId}`);
    callback([]);
  });
};

/**
 * Historial de informes generados para Modular-1
 * Almacena hasta 15 snapshots completos (informe base, dictado, resultados, modos)
 */
export const saveModular1GenerationSnapshot = async (userId: string, snapshot: any) => {
  if (!userId || userId === 'guest_dev_offline') return;
  try {
    const docRef = doc(db, 'modular1_generation_log', userId);
    const snap = await getDoc(docRef);
    const current = snap.exists() ? (snap.data().entries || []) : [];
    const next = [...current, snapshot].slice(-15);
    await setDoc(docRef, { entries: next, updatedAt: serverTimestamp() }, { merge: true });
  } catch (e) {
    handleFirestoreError(e, OperationType.WRITE, `modular1_generation_log/${userId}`);
  }
};

export const subscribeToModular1GenerationLog = (userId: string, callback: (entries: any[]) => void) => {
  if (!userId || userId === 'guest_dev_offline') return () => {};
  const docRef = doc(db, 'modular1_generation_log', userId);
  return onSnapshot(docRef, (snap) => {
    callback(snap.exists() ? (snap.data().entries || []) : []);
  }, (error) => {
    handleFirestoreError(error, OperationType.GET, `modular1_generation_log/${userId}`);
    callback([]);
  });
};

/**
 * SEMÁFORO GLOBAL (Distributed Lock)
 * Evita que múltiples dispositivos procesen la misma tarea simultáneamente.
 */
export const acquireGlobalLock = async (userId: string, lockId: string, ttlMs: number = 10000): Promise<boolean> => {
  if (!userId || userId === 'guest_dev_offline') return true;
  
  const now = Date.now();
  const lockRef = doc(db, "users", userId, "locks", lockId);
  
  try {
    const snap = await getDoc(lockRef);
    if (snap.exists()) {
      const data = snap.data();
      // Si el bloqueo ha expirado o el poseedor es este mismo TAB_ID, lo renovamos
      if (now < data.expiry && data.holder !== TAB_ID) {
        console.log(`[GlobalLock] Tarea bloqueada por otro dispositivo (${data.holder})`);
        return false;
      }
    }
    
    await setDoc(lockRef, {
      holder: TAB_ID,
      expiry: now + ttlMs,
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.warn("[GlobalLock] Error al verificar semáforo global:", error);
    return true; // En caso de duda, permitimos la ejecución para no bloquear al usuario
  }
};
